import React from 'react';
import { cn } from './cn';

/**
 * Full-row action for queues and drill-in lists. Owns the native button,
 * separators, hover/focus surface, spacing, and trailing-action placement;
 * screens supply only their domain summary and action copy.
 */
export default function ActionRow({
  as: Component = 'li',
  layout = 'grid',
  selected,
  divider = true,
  children,
  leading,
  trailing,
  actions,
  onClick,
  ariaLabel,
  'aria-label': nativeAriaLabel,
  className,
  buttonClassName,
  ...props
}) {
  return (
    <Component
      className={cn(
        'group relative bg-bun-surface transition-colors',
        (leading || actions) && 'flex items-center',
        divider && 'border-b border-bun-line-soft last:border-b-0',
        selected !== undefined && 'border-l-2 border-l-transparent',
        selected ? 'border-l-bun-blue bg-bun-wash hover:bg-bun-wash focus-within:bg-bun-wash'
          : 'hover:bg-bun-mist focus-within:bg-bun-mist',
        className
      )}
    >
      {leading && <div className="shrink-0 pl-4 sm:pl-[18px]">{leading}</div>}
      <button
        {...props}
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? nativeAriaLabel}
        className={cn(
          'min-h-11 w-full cursor-pointer items-center',
          (leading || actions) && 'min-w-0 flex-1',
          layout === 'flex' ? 'flex' : 'grid grid-cols-[minmax(0,1fr)_auto]',
          'gap-x-3 bg-transparent px-4 py-3 text-left font-instrument font-book text-bun-slate sm:px-[18px]',
          'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-bun-blue',
          buttonClassName
        )}
      >
        {children}
        {trailing ? (
          <span className="col-start-2 row-start-1 justify-self-end" aria-hidden="true">
            {trailing}
          </span>
        ) : null}
      </button>
      {actions && <div className="flex shrink-0 items-center gap-2 pr-[18px]">{actions}</div>}
    </Component>
  );
}
